import axios from "axios";

/**
 * Standard farmer fetch utility.
 *
 * ── API Contract (implement / enforce in backend) ───────────────────────────
 *
 * Request:  POST /api/farmer/searchFarmer
 * Body: {
 *   page      : number   — 1-based page index          (required for pagination)
 *   limit     : number   — records per page            (required for pagination)
 *   search    : string   — free-text across name/mobile/village
 *   state     : string   — exact state name
 *   district  : string   — exact district name
 *   crop      : string   — exact crop name
 *   category  : string   — "Gold" | "Silver" | "Regular"
 *   sortBy    : string   — field name to sort by
 *   sortDir   : string   — "asc" | "desc"
 * }
 *
 * Response (preferred — paginated):
 * {
 *   data       : Farmer[]   — current page records
 *   total      : number     — total matching records (across all pages)
 *   page       : number     — current page
 *   totalPages : number
 * }
 *
 * Response (legacy / current — full array):
 *   Farmer[]   — all matching records returned at once
 *
 * ── Behaviour when NO filters are provided ──────────────────────────────────
 * Backend MUST return all farmers (paginated by page/limit) when only
 * page + limit are passed and no filter fields are set.
 * Frontend always sends page + limit so this case is unambiguous.
 */
const getFarmers = async (params = {}) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/farmer/searchFarmer`,
      params
    );
    const raw = res.data;

    // Paginated response: { data: [...], total: N, ... }
    if (raw && !Array.isArray(raw) && Array.isArray(raw.data)) {
      return {
        data       : raw.data,
        total      : raw.total      ?? raw.data.length,
        totalPages : raw.totalPages ?? null,
      };
    }

    // Legacy response: bare array (full list or filtered slice)
    if (Array.isArray(raw)) {
      return {
        data       : raw,
        total      : raw.length,
        totalPages : null,         // unknown — caller must compute if needed
      };
    }

    return { data: [], total: 0, totalPages: null };
  } catch {
    return { data: [], total: 0, totalPages: null };
  }
};

export default getFarmers;
